import React from 'react';
import './LPPositionCard.scss';

const formatNumber = (num) => {
    if (num === null || num === undefined) return 'TBD';
    if (typeof num !== 'number' || isNaN(num)) return '0';
    return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

const formatCurrency = (value) => {
    if (typeof value !== 'number' || isNaN(value)) return '$0.00';
    return `$${value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const LOCK_TYPE_CONFIG = {
    forever: {
        label: 'Forever Locked',
        icon: '🔒',
        className: 'lock-forever',
    },
    timeLocked: {
        label: 'Time Locked',
        icon: '⏳',
        className: 'lock-time',
    },
    upcoming: {
        label: 'Upcoming Lock',
        icon: '🔜',
        className: 'lock-upcoming',
    },
};

const LPPositionCard = ({
    platform,
    platformIcon,
    lockType, // 'forever' | 'timeLocked' | 'upcoming'
    tokens, // [{ symbol, amount, iconUrl }]
    storedAt,
    proofUrl,
    proofLabel = 'View Proof ->',
    note,
    animDelay = 0,
    tokenPrices = {}, // { symbol: priceUSD }
    isLoadingPrice = false,
}) => {
    const lockConfig = LOCK_TYPE_CONFIG[lockType] || LOCK_TYPE_CONFIG.timeLocked;

    // Only show a total when the whole position is known and priced.
    let totalValueUSD = 0;
    let hasAnyValue = false;
    let hasUnknownAmount = false;
    let hasMissingPrice = false;
    const tokenValues = tokens.map(t => {
        if (t.amount === null || t.amount === undefined) {
            hasUnknownAmount = true;
            return null;
        }

        const price = tokenPrices[t.symbol];
        if (typeof price === 'number' && price > 0 && typeof t.amount === 'number') {
            const value = t.amount * price;
            totalValueUSD += value;
            hasAnyValue = true;
            return value;
        }

        if (typeof t.amount === 'number' && t.amount > 0) {
            hasMissingPrice = true;
        }
        return null;
    });
    const canShowTotalValue = hasAnyValue && !hasUnknownAmount && !hasMissingPrice;

    return (
        <div
            className={`lp-position-card ${lockConfig.className}`}
            style={{ animationDelay: `${animDelay}ms` }}
        >
            {/* Accent bar */}
            <div className="lp-accent-bar" />

            {/* Header */}
            <div className="lp-card-header">
                <div className="lp-platform">
                    {platformIcon && (
                        <img src={platformIcon} alt={platform} className="lp-platform-icon" />
                    )}
                    <span className="lp-platform-name">{platform}</span>
                </div>
                <span className={`lp-lock-badge ${lockConfig.className}`}>
                    <span className="lp-lock-icon">{lockConfig.icon}</span>
                    {lockConfig.label}
                </span>
            </div>

            {/* Token pair display */}
            <div className="lp-token-pair">
                <div className="lp-token-icons">
                    {tokens.map((t, i) => (
                        <img
                            key={i}
                            src={t.iconUrl}
                            alt={t.symbol}
                            className="lp-token-icon"
                            style={{ zIndex: tokens.length - i }}
                        />
                    ))}
                </div>
                <div className="lp-token-amounts">
                    {tokens.map((t, i) => (
                        <div key={i} className="lp-token-row">
                            <span className="lp-token-amount">{formatNumber(t.amount)}</span>
                            <span className="lp-token-symbol">{t.symbol}</span>
                            {/* Per-token USD value */}
                            {isLoadingPrice ? (
                                <span className="lp-token-value loading">…</span>
                            ) : tokenValues[i] !== null ? (
                                <span className="lp-token-value">{formatCurrency(tokenValues[i])}</span>
                            ) : t.amount === null ? (
                                <span className="lp-token-value muted">TBD</span>
                            ) : null}
                        </div>
                    ))}
                </div>
            </div>

            {/* Total position value */}
            {!isLoadingPrice && canShowTotalValue && (
                <div className="lp-total-value">
                    <span className="lp-total-label">Position Value</span>
                    <span className="lp-total-amount">{formatCurrency(totalValueUSD)}</span>
                </div>
            )}
            {isLoadingPrice && (
                <div className="lp-total-value">
                    <span className="lp-total-label">Position Value</span>
                    <span className="lp-total-amount loading">Loading…</span>
                </div>
            )}

            {/* Footer: storage + proof */}
            <div className="lp-card-footer">
                <span className="lp-stored-at">
                    📍 <span className="lp-stored-label">{storedAt}</span>
                </span>
                {proofUrl && (
                    <a
                        href={proofUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="lp-proof-link"
                    >
                        {proofLabel}
                    </a>
                )}
            </div>

            {note && <p className="lp-note">{note}</p>}
        </div>
    );
};

export default LPPositionCard;
